import { ledger } from './browser.js';

/**
 * The photo upload queue — design v1.1 §5, the G10 states made real.
 *
 * Every entry is VISIBLY in one of four states (queued / uploading / failed / done) and
 * the strip renders them; a dropped upload is a chip that says "failed · retry", never a
 * photo that quietly does not exist on the server. Failures are ledgered with the HTTP
 * status handed to the classifier (pass what the caller knows).
 *
 * DELIBERATELY IN-MEMORY. D7 defers offline-first: the queue survives navigation within
 * the app (module singleton) and does NOT survive tab close — `pendingCount()` feeds the
 * `beforeunload` warning that says so. Persisting blobs to IndexedDB is the offline
 * increment's job, not a thing to half-do here.
 *
 * The PUT function is injected so the queue is testable under Node with no network and
 * no jsdom: the tests exercise ordering, retry, concurrency and state transitions — the
 * parts that can be silently wrong.
 */

export type UploadStatus = 'queued' | 'uploading' | 'failed' | 'done';

export interface UploadEntry {
  /** The R2 object key — the stable identity everywhere else in the system. */
  key: string;
  status: UploadStatus;
  /** User-facing message for the failed state. */
  message?: string;
}

export type PutFn = (url: string, body: Blob) => Promise<void>;

const TIMEOUT_MS = 30_000;
const CONCURRENCY = 2;

/**
 * PUT bytes to a presigned URL — the one place in the browser that knows how.
 *
 * ⚠️ THE CONTENT TYPE MUST MATCH WHAT THE SERVER SIGNED, EXACTLY. R2 verifies it as part of
 * the signature, so a mismatch is a 403 that reads like a credentials problem and is not one.
 * That is why this takes the type as an argument instead of reading `body.type`: a File the
 * browser labels `image/heic` would then be PUT as HEIC against a URL signed for JPEG, and
 * the failure would appear only on the phones that produce HEIC.
 *
 * Two callers, two purposes (§9-E's boundary — the allowlist is the security surface):
 * photos through the queue below, and a return-label PDF straight from the box screen.
 */
export async function putSigned(url: string, body: Blob, contentType = 'image/jpeg'): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': contentType },
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  if (!response.ok) {
    throw Object.assign(new Error(`upload failed with ${String(response.status)}`), {
      httpStatus: response.status,
    });
  }
}

/** Default PUT for the photo queue: JPEG, matching what `uploadUrls` signs. */
export const fetchPut: PutFn = (url, body) => putSigned(url, body, 'image/jpeg');

interface Job {
  key: string;
  url: string;
  body: Blob;
  status: UploadStatus;
  message?: string;
}

export interface UploadQueue {
  enqueue(key: string, url: string, body: Blob): void;
  retry(key: string): void;
  remove(key: string): void;
  entries(): UploadEntry[];
  /** queued + uploading — what beforeunload warns about. */
  pendingCount(): number;
  subscribe(listener: () => void): () => void;
}

export function createUploadQueue(put: PutFn = fetchPut): UploadQueue {
  const jobs = new Map<string, Job>();
  const listeners = new Set<() => void>();
  let inFlight = 0;

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  const pump = (): void => {
    while (inFlight < CONCURRENCY) {
      const next = [...jobs.values()].find((j) => j.status === 'queued');
      if (!next) return;
      next.status = 'uploading';
      inFlight += 1;
      notify();
      void put(next.url, next.body)
        .then(() => {
          next.status = 'done';
        })
        .catch((error: unknown) => {
          // The chip shows "failed · retry" — that is the user-visible signal; the
          // ledger entry is the persistent one. Both, per G10.
          const status = (error as { httpStatus?: number }).httpStatus;
          const entry = ledger.record(error, 'upload:photo', {
            notified: true,
            ...(status !== undefined ? { classify: { httpStatus: status } } : {}),
            context: { key: next.key },
          });
          next.status = 'failed';
          next.message = entry.userMessage;
        })
        .finally(() => {
          inFlight -= 1;
          notify();
          pump();
        });
    }
  };

  return {
    enqueue(key, url, body) {
      jobs.set(key, { key, url, body, status: 'queued' });
      notify();
      pump();
    },
    retry(key) {
      const job = jobs.get(key);
      if (job && job.status === 'failed') {
        job.status = 'queued';
        delete job.message;
        notify();
        pump();
      }
    },
    remove(key) {
      // Removing an in-flight job lets the PUT finish against R2 — an orphaned object
      // with no row is invisible garbage, not a defect, and aborting mid-PUT can leave
      // the same orphan anyway. The entry disappears from the UI either way.
      jobs.delete(key);
      notify();
    },
    entries() {
      return [...jobs.values()].map(({ key, status, message }) => ({
        key,
        status,
        ...(message !== undefined ? { message } : {}),
      }));
    },
    pendingCount() {
      return [...jobs.values()].filter((j) => j.status === 'queued' || j.status === 'uploading').length;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The app-wide queue. Screens import this; tests build their own with a fake PUT. */
export const uploadQueue = createUploadQueue();
